/**
 * Today for one group, on one line (user, 02/11/2026: "really messy and not
 * readable"): "Today · 8 drops → LIST" for one list; for the two all-lists
 * ways the sends in words and today's lists as chips (the first few, then
 * "+N"); drops fixed to a list apart; the verdict a quiet "List verdict
 * off ⓘ" at the end, its sentence behind the ⓘ. The played grid and its
 * Failure example went on 23/10/2026 ("Hide the animations").
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { HintDot } from '@/components/ui';

import type { ListMode } from '../list-choice/list-mode';
import { type MatrixDrop, matrixSummary } from '../list-choice/matrix-model';

const CHIP = 'max-w-[18rem] truncate rounded-md border border-border/60 bg-background px-1.5 py-px text-[11px] font-medium text-foreground';
const CHIPS_SHOWN = 3;

interface Props {
  mode: ListMode;
  drops: MatrixDrop[];
  lists: number;
  names: string[];
  /** Drops fixed to one list (they keep it whatever the choice). */
  fixed: number;
  verdict: { on: boolean; words: string };
}

const dropsWord = (n: number) => `${n} drop${n === 1 ? '' : 's'}`;

export const TodayLine: FC<Props> = ({ mode, drops, lists, names, fixed, verdict }) => {
  const ranked = drops.filter((d) => d.rank !== undefined).length;
  const shown = names.slice(0, Math.max(0, lists));
  const oneList = mode === 'one' && drops.length > 0 && shown.length === 1;
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs" data-testid="today-line">
      <span className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">Today</span>
      {oneList ? (
        <>
          <span className="text-foreground/80">{dropsWord(drops.length)} →</span>
          <span className={CHIP} title={shown[0]}>{shown[0]}</span>
        </>
      ) : (
        <span className="text-foreground/80">{matrixSummary(mode, drops.length, lists, ranked)}</span>
      )}
      {mode !== 'one' ? <ListChips names={shown} /> : null}
      {fixed > 0 ? <span className="text-muted-foreground">· {dropsWord(fixed)} fixed to a list</span> : null}
      <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-muted-foreground" data-testid="verdict-note">
        List verdict {verdict.on ? 'on' : 'off'}
        <HintDot label={verdict.words} />
      </span>
    </div>
  );
};

/** Today's lists in order — the first few, then "+N more" (every name in its title). */
const ListChips: FC<{ names: string[] }> = ({ names }) => {
  const rest = names.length - CHIPS_SHOWN;
  return (
    <>
      {names.slice(0, CHIPS_SHOWN).map((name, i) => (
        <span key={`${i}:${name}`} className={CHIP} title={name}>
          <span className="mr-1 text-muted-foreground">#{i + 1}</span>
          {name}
        </span>
      ))}
      {rest > 0 ? (
        <span className="text-[11px] text-muted-foreground" title={names.slice(CHIPS_SHOWN).join('\n')}>
          +{rest} more
        </span>
      ) : null}
    </>
  );
};
